import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Paper,
  Chip,
  CircularProgress,
  Button,
  Pagination,
  Stack,
  Divider,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Search,
  ExpandMore,
  CalendarToday,
  Person,
  Settings,
} from "@mui/icons-material";
import { getAdminLogs, getLogStats } from "../services/logService";

const AdminLogs = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({});
  const [filters, setFilters] = useState({
    page: 1,
    limit: 50,
    action_type: "",
    start_date: "",
    end_date: "",
    search: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchLogs = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await getAdminLogs(filters);
      setLogs(response.data.logs || []);
    } catch (error) {
      console.error("Failed to fetch logs:", error);
      setError("Failed to load logs. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await getLogStats({
        start_date: filters.start_date,
        end_date: filters.end_date,
      });
      setStats(response.data || {});
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  };

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [filters]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: 1, // Reset to first page when filters change
    }));
  };

  const handlePageChange = (event, value) => {
    handleFilterChange("page", value);
  };

  const getActionTypeColor = (actionType) => {
    const colors = {
      item_create: "success",
      item_update: "info",
      item_delete: "error",
      item_duplicate: "warning",
      item_update_flags: "secondary",
      item_reorder: "primary",
      item_category_remove: "default",
    };
    return colors[actionType] || "default";
  };

  const formatActionType = (actionType) => {
    const formatted = actionType
      .replace(/_/g, " ")
      .replace(/\b\w/g, (l) => l.toUpperCase());
    return formatted;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Admin Action Logs
      </Typography>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Search users or actions"
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
                InputProps={{
                  startAdornment: (
                    <Search sx={{ mr: 1, color: "text.secondary" }} />
                  ),
                }}
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={filters.start_date}
                onChange={(e) =>
                  handleFilterChange("start_date", e.target.value)
                }
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={filters.end_date}
                onChange={(e) => handleFilterChange("end_date", e.target.value)}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Action Type</InputLabel>
                <Select
                  value={filters.action_type}
                  label="Action Type"
                  onChange={(e) =>
                    handleFilterChange("action_type", e.target.value)
                  }
                >
                  <MenuItem value="">All Actions</MenuItem>
                  <MenuItem value="item_create">Item Create</MenuItem>
                  <MenuItem value="item_update">Item Update</MenuItem>
                  <MenuItem value="item_delete">Item Delete</MenuItem>
                  <MenuItem value="item_duplicate">Item Duplicate</MenuItem>
                  <MenuItem value="item_update_flags">Update Flags</MenuItem>
                  {/* <MenuItem value="item_reorder">Reorder Items</MenuItem> */}
                  {/* <MenuItem value="item_category_remove">
                    Remove Item from Category
                  </MenuItem> */}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Items per page</InputLabel>
                <Select
                  value={filters.limit}
                  label="Items per page"
                  onChange={(e) => handleFilterChange("limit", e.target.value)}
                >
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Stats */}
      {stats && (stats.total_actions > 0 || stats.unique_users > 0) && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, textAlign: "center" }} elevation={1}>
              <Typography variant="h4" color="primary">
                {stats.total_actions || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Actions
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, textAlign: "center" }} elevation={1}>
              <Typography variant="h4" color="secondary">
                {stats.unique_users || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Unique Users
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, textAlign: "center" }} elevation={1}>
              <Typography variant="h4" color="info.main">
                {stats.action_stats?.length || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Action Types
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, textAlign: "center" }} elevation={1}>
              <Typography variant="h4" color="success.main">
                {logs.pagination?.pages || 1}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Pages
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Logs List */}
          <Stack spacing={2}>
            {logs.length === 0 ? (
              <Alert severity="info">
                No logs found matching your criteria.
              </Alert>
            ) : (
              logs.map((log) => (
                <Accordion key={log.id} elevation={2}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        width: "100%",
                      }}
                    >
                      <Chip
                        label={formatActionType(log.action_type)}
                        color={getActionTypeColor(log.action_type)}
                        size="small"
                      />
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle1">
                          <Person
                            sx={{
                              fontSize: 16,
                              verticalAlign: "middle",
                              mr: 0.5,
                            }}
                          />
                          {log.user_name || "Unknown User"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {log.user_email}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        <CalendarToday
                          sx={{
                            fontSize: 14,
                            verticalAlign: "middle",
                            mr: 0.5,
                          }}
                        />
                        {new Date(log.created_at).toLocaleString()}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="subtitle2" gutterBottom>
                      Request Data:
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        bgcolor: "grey.50",
                        maxHeight: 300,
                        overflow: "auto",
                      }}
                    >
                      <pre style={{ margin: 0, fontSize: "0.875rem" }}>
                        {JSON.stringify(log.request_data, null, 2)}
                      </pre>
                    </Paper>
                  </AccordionDetails>
                </Accordion>
              ))
            )}
          </Stack>

          {/* Pagination */}
          {logs.pagination && logs.pagination.pages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
              <Pagination
                count={logs.pagination.pages}
                page={logs.pagination.page}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default AdminLogs;
